import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fastapiService } from '../../services/fastapiService';
import { getEnvVar } from '../../config/env';

type ToolRow = {
  tool_name: string;
  display_name?: string;
  description?: string;
  category?: string;
  execution_mode?: string;
  enabled?: boolean;
  risk_level?: string;
  input_schema?: Record<string, unknown>;
  source_endpoint?: string;
  source_service?: string;
  required_scopes?: unknown;
};

export const JarvisToolsPage: React.FC = () => {
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [onlyEnabled, setOnlyEnabled] = useState<'all' | 'on' | 'off'>('all');
  const [selected, setSelected] = useState<ToolRow | null>(null);
  const [descDraft, setDescDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState<any>(null);
  const [workspaceId, setWorkspaceId] = useState('');
  const [profileId, setProfileId] = useState('');
  const [payloadJson, setPayloadJson] = useState('{}');
  const [dryRun, setDryRun] = useState(true);
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fastapiService.getJarvisTools();
      setTools(res.tools || []);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Failed to load tools');
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    tools.forEach((t) => {
      if (t.category) s.add(String(t.category));
    });
    return Array.from(s).sort();
  }, [tools]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return tools.filter((t) => {
      if (onlyEnabled === 'on' && t.enabled === false) return false;
      if (onlyEnabled === 'off' && t.enabled !== false) return false;
      if (cat && String(t.category) !== cat) return false;
      if (!qq) return true;
      const blob = `${t.tool_name} ${t.display_name || ''} ${t.description || ''}`.toLowerCase();
      return blob.includes(qq);
    });
  }, [tools, q, cat, onlyEnabled]);

  useEffect(() => {
    if (!selected) {
      setDescDraft('');
      setSchema(null);
      return;
    }
    setDescDraft(selected.description || '');
    void (async () => {
      try {
        const s = await fastapiService.getJarvisToolSchema(selected.tool_name);
        setSchema(s);
      } catch {
        setSchema(null);
      }
    })();
  }, [selected]);

  const saveDescription = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await fastapiService.updateJarvisTool(selected.tool_name, { description: descDraft });
      await load();
      const updated = (await fastapiService.getJarvisTools()).tools?.find((x: ToolRow) => x.tool_name === selected.tool_name);
      if (updated) setSelected(updated);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!selected) return;
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(payloadJson || '{}');
    } catch {
      setError('Payload must be valid JSON');
      return;
    }
    if (!workspaceId.trim() || !profileId.trim()) {
      setError('Workspace ID and user profile ID are required');
      return;
    }
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const r = await fastapiService.testJarvisTool(selected.tool_name, {
        workspace_id: workspaceId.trim(),
        user_profile_id: profileId.trim(),
        payload,
        dry_run: dryRun,
      });
      setTestResult(r);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const downloadJson = async (kind: 'catalog' | 'manifest' | 'openai' | 'mcp') => {
    try {
      let data: any;
      if (kind === 'catalog') data = await fastapiService.getPortableCatalogFile();
      else if (kind === 'manifest') data = await fastapiService.getToolsManifest();
      else if (kind === 'openai') data = await fastapiService.getToolsManifestOpenAI();
      else data = await fastapiService.getToolsManifestMcp();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm-tools-${kind}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.error || e?.message || 'Download failed');
    }
  };

  const baseUrl = (() => {
    try {
      return getEnvVar('VITE_FASTAPI_BASE_URL').replace(/\/$/, '');
    } catch {
      return '';
    }
  })();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Jarvis tools</h1>
        <p className="mt-1 text-sm text-admin-600">
          AI-visible descriptions, schemas, and test runs against real workspace data. Write tools can change production data when
          dry-run is off — use a sandbox workspace when possible.
        </p>
      </div>

      <div className="admin-card p-4 flex flex-wrap gap-2 items-center">
        <span className="text-sm text-admin-700 mr-2">Export:</span>
        <button
          type="button"
          className="admin-button-secondary px-3 py-1.5 text-sm rounded border border-admin-200"
          onClick={() => void downloadJson('catalog')}
        >
          Catalog JSON
        </button>
        <button
          type="button"
          className="admin-button-secondary px-3 py-1.5 text-sm rounded border border-admin-200"
          onClick={() => void downloadJson('manifest')}
        >
          Manifest (merged)
        </button>
        <button
          type="button"
          className="admin-button-secondary px-3 py-1.5 text-sm rounded border border-admin-200"
          onClick={() => void downloadJson('openai')}
        >
          OpenAI tools
        </button>
        <button
          type="button"
          className="admin-button-secondary px-3 py-1.5 text-sm rounded border border-admin-200"
          onClick={() => void downloadJson('mcp')}
        >
          MCP shape
        </button>
        <span className="text-xs text-admin-500 ml-2 break-all">
          Public catalog file (GitHub): <code className="bg-admin-100 px-1 rounded">tools/catalog/crm_tools.json</code> · API:{' '}
          <code className="bg-admin-100 px-1 rounded">{baseUrl}/api/v1/tools/manifest</code>
        </span>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800" role="alert">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-admin-600">Search</label>
              <input
                className="mt-1 w-full rounded border border-admin-200 px-2 py-1 text-sm"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="name, description…"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-admin-600">Category</label>
              <select className="mt-1 rounded border border-admin-200 px-2 py-1 text-sm" value={cat} onChange={(e) => setCat(e.target.value)}>
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-admin-600">Enabled</label>
              <select
                className="mt-1 rounded border border-admin-200 px-2 py-1 text-sm"
                value={onlyEnabled}
                onChange={(e) => setOnlyEnabled(e.target.value as any)}
              >
                <option value="all">All</option>
                <option value="on">Enabled</option>
                <option value="off">Disabled</option>
              </select>
            </div>
            <button
              type="button"
              className="admin-button px-3 py-1.5 text-sm rounded"
              onClick={() => void load()}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
          <div className="max-h-[560px] overflow-y-auto border border-admin-200 rounded">
            {loading ? (
              <p className="p-4 text-sm text-admin-600">Loading…</p>
            ) : (
              <ul className="divide-y divide-admin-100">
                {filtered.map((t) => (
                  <li key={t.tool_name}>
                    <button
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-admin-50 ${selected?.tool_name === t.tool_name ? 'bg-admin-100' : ''}`}
                      onClick={() => setSelected(t)}
                    >
                      <div className="font-medium text-admin-900">{t.tool_name}</div>
                      <div className="text-xs text-admin-500">
                        {t.execution_mode || '—'} · {t.category || '—'} {t.enabled === false ? '· disabled' : ''}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="admin-card p-4 space-y-4">
          {!selected ? (
            <p className="text-sm text-admin-600">Select a tool.</p>
          ) : (
            <>
              <div>
                <h2 className="text-lg font-semibold text-admin-900">{selected.tool_name}</h2>
                <p className="text-xs text-admin-500 mt-1">
                  {selected.display_name} · risk: {selected.risk_level || '—'} · {selected.source_service || ''}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-admin-600">AI-visible description</label>
                <textarea
                  className="mt-1 w-full min-h-[120px] rounded border border-admin-200 px-2 py-2 text-sm font-mono"
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="admin-button px-3 py-1.5 text-sm rounded"
                    disabled={saving}
                    onClick={() => void saveDescription()}
                  >
                    Save description
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-admin-800">Schema / source</h3>
                <pre className="mt-2 max-h-48 overflow-auto rounded bg-admin-900/95 text-admin-50 text-xs p-3">
                  {JSON.stringify(schema?.merged?.input_schema || selected.input_schema || {}, null, 2)}
                </pre>
              </div>

              <div className="border-t border-admin-200 pt-4 space-y-2">
                <h3 className="text-sm font-semibold text-admin-800">Test runner</h3>
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Write tools perform real mutations when dry-run is off. Prefer a test workspace.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-admin-600">Workspace ID</label>
                    <input
                      className="mt-1 w-full rounded border border-admin-200 px-2 py-1 text-xs font-mono"
                      value={workspaceId}
                      onChange={(e) => setWorkspaceId(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-admin-600">User profile ID</label>
                    <input
                      className="mt-1 w-full rounded border border-admin-200 px-2 py-1 text-xs font-mono"
                      value={profileId}
                      onChange={(e) => setProfileId(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-admin-600">JSON payload</label>
                  <textarea
                    className="mt-1 w-full min-h-[100px] rounded border border-admin-200 px-2 py-1 text-xs font-mono"
                    value={payloadJson}
                    onChange={(e) => setPayloadJson(e.target.value)}
                  />
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
                  Dry run (recommended)
                </label>
                <div>
                  <button
                    type="button"
                    className="admin-button px-3 py-1.5 text-sm rounded"
                    disabled={testing}
                    onClick={() => void runTest()}
                  >
                    {testing ? 'Running…' : 'Run test'}
                  </button>
                </div>
                {testResult && (
                  <pre className="mt-2 max-h-56 overflow-auto rounded bg-admin-50 border border-admin-200 text-xs p-2">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
